import clsx from 'clsx';
import { ComponentProps, FC, JSX, ReactNode } from 'react';

export interface TdsLinkProps extends ComponentProps<'a'> {
  variant?: 'inline' | 'standalone' | 'inline-neutral' | 'standalone-neutral';
  size?: 'sm' | 'md' | 'lg';
  iconPosition?: 'left' | 'right';
  icon?: ReactNode;
  external?: boolean;
  compact?: boolean;
  href: string;
  renderLink?: (props: TdsLinkProps) => JSX.Element;
  light?: boolean;
}

export const Link: FC<TdsLinkProps> = ({
  href,
  children,
  variant = 'inline',
  size,
  iconPosition,
  icon,
  external,
  compact,
  className,
  renderLink,
  light,
  ...rest
}) => {
  const classes = clsx(
    'group',
    'tds-link',
    {
      [`tds-link--${variant}`]: variant,
      [`tds-link--icon-${iconPosition}`]: iconPosition,
      'tds-link--compact': compact,
      '!text-white hover:!text-white focus-within:!text-black': light,
    },
    className,
  );

  const content = (
    <>
      {!external && iconPosition === 'left' && icon}
      {children}
      {!external && iconPosition === 'right' && icon}
      {external && (
        <ExternalIcon
          className={
            light
              ? '!fill-white group-focus-within:!fill-black group-hover:group-focus-within:!fill-white'
              : undefined
          }
        />
      )}
    </>
  );

  const component = renderLink ? (
    renderLink({
      ...rest,
      className: classes,
      href,
      children: content,
      ...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {}),
    })
  ) : (
    <a
      {...rest}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      href={href}
      className={classes}
    >
      {content}
    </a>
  );

  if (size) {
    return <div className={`tds-body-${size}`}>{component}</div>;
  }

  return component;
};

const ExternalIcon = (props: ComponentProps<'path'>) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="25"
    height="25"
    viewBox="0 0 25 25"
    className="shrink-0"
  >
    <path
      {...props}
      fillRule="evenodd"
      clipRule="evenodd"
      d="M21.5 3.5H13.5V5.5H18.0858L8.79291 14.7929L10.2071 16.2071L19.5 6.91421V11.5H21.5V3.5ZM5.5 5.5C4.39543 5.5 3.5 6.39543 3.5 7.5V19.5C3.5 20.6046 4.39543 21.5 5.5 21.5H17.5C18.6046 21.5 19.5 20.6046 19.5 19.5V14.5H17.5V19.5H5.5V7.5L10.5 7.5V5.5H5.5Z"
    />
  </svg>
);
